import { useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { ChevronDown, LogOut, UserRound } from "lucide-react";
import { domainsInGroup } from "../routes/domains";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import UserAvatar from "./people/UserAvatar";
import { openOnScriptedClick } from "./menuTrigger";

/** The account pages (the Agents section's tabs), named for the menu:
 * Overview reads as "Agents" here. Hidden from agent sessions, as the
 * Agents section always was — My token would hand an agent its token. */
function accountLinks(isAgentSession) {
  if (isAgentSession) return [];
  return domainsInGroup("account").flatMap((domain) =>
    domain.subNav.map((entry) => ({
      key: entry.key,
      path: entry.path,
      label: entry.key === "overview" ? domain.label : entry.label,
    })),
  );
}

/**
 * Account things under your avatar (board #104): your picture, Household,
 * Agents, My token and Log out. From lg a menu on the avatar, with the
 * Dark mode switch beside it in the header (Brandon: next to the picture,
 * not inside the menu); below lg (`stacked`) the same items as a headed
 * list in the hamburger panel, the Dark mode row among them.
 */
function AccountMenu({
  me = null,
  isAgentSession = false,
  stacked = false,
  theme,
  onToggleTheme,
  onPicture,
  onLogout,
  triggerRef = null,
}) {
  const links = accountLinks(isAgentSession);
  // Controlled, so a scripted click can open it (openOnScriptedClick).
  const [open, setOpen] = useState(false);

  if (stacked) {
    return (
      <section
        aria-labelledby="nav-group-account"
        className="flex flex-col gap-1"
      >
        <h2
          id="nav-group-account"
          className="px-3 pb-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase"
        >
          Account
        </h2>
        {me && (
          <button
            type="button"
            onClick={onPicture}
            className="flex min-h-11 items-center gap-2 rounded-md px-3 py-2 text-left font-semibold hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            <UserAvatar user={me} size="sm" decorative />
            Profile &amp; picture
          </button>
        )}
        {links.map((link) => (
          <Link
            key={link.key}
            to={link.path}
            className="flex min-h-11 items-center rounded-md px-3 py-2 font-semibold text-foreground no-underline hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            {link.label}
          </Link>
        ))}
        {/* The label wraps the switch: the whole ≥ 44px row is the tap
            target, not just the switch or the words. */}
        <Label className="flex min-h-11 cursor-pointer items-center gap-3 rounded-md px-3 font-semibold hover:bg-muted">
          <Switch
            checked={theme === "dark"}
            onCheckedChange={() => onToggleTheme()}
          />
          Dark mode
        </Label>
        <Button
          variant="outline"
          className="mx-3 h-11 self-start"
          onClick={onLogout}
        >
          Log out
        </Button>
      </section>
    );
  }

  return (
    <DropdownMenu modal={false} open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        ref={triggerRef}
        onClick={openOnScriptedClick(setOpen)}
        aria-label={me ? `Account: ${me.username}` : "Account"}
        className="flex items-center gap-1 rounded-full p-0.5 pr-1.5 hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      >
        {me ? (
          <UserAvatar user={me} size="md" decorative />
        ) : (
          <UserRound
            aria-hidden="true"
            className="size-8 rounded-full bg-muted p-1.5"
          />
        )}
        <ChevronDown
          aria-hidden="true"
          className="size-4 text-muted-foreground"
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56">
        {me && (
          <DropdownMenuLabel className="font-normal text-muted-foreground">
            Signed in as{" "}
            <span className="font-semibold text-foreground">{me.username}</span>
          </DropdownMenuLabel>
        )}
        {me && (
          <DropdownMenuItem onSelect={onPicture}>
            Profile &amp; picture
          </DropdownMenuItem>
        )}
        {links.map((link) => (
          <DropdownMenuItem key={link.key} asChild>
            <Link to={link.path} className="no-underline">
              {link.label}
            </Link>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onLogout}>
          <LogOut aria-hidden="true" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

AccountMenu.propTypes = {
  me: PropTypes.shape({
    username: PropTypes.string,
    avatar_url: PropTypes.string,
  }),
  isAgentSession: PropTypes.bool,
  stacked: PropTypes.bool,
  /** The phone panel's Dark mode row (`stacked`); from lg it's in the header. */
  theme: PropTypes.string,
  onToggleTheme: PropTypes.func,
  onPicture: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired,
  /** The menu's button, e.g. for a dialog to hand focus back to. */
  triggerRef: PropTypes.shape({ current: PropTypes.any }),
};

export default AccountMenu;
